import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  OrgLocation,
  OrgLocationContextType,
} from '@/features/organizations/organizations.types';
import { persistStorageService } from '@/features/auth/persist-storage';
import { locationAtom } from '@/features/locations/location-atom';
import { organizationAtom } from '@/features/organizations/organization-atom';
import { useAtom } from 'jotai';
import { useGetOrganizations } from '@/features/organizations/organizations.hook';
import { useGetLocations } from '@/features/locations/locations.hook';
import { queryClient } from '@/lib/react-query';

// Create the context with a default undefined value
const OrgLocationContext = createContext<OrgLocationContextType | undefined>(
  undefined,
);

// Provider props type
interface OrgLocationProviderProps {
  children: ReactNode;
  defaultOrganisation?: OrgLocation;
  defaultLocation?: OrgLocation;
}

// Create the provider component
export const OrgLocationProvider: React.FC<OrgLocationProviderProps> = ({
  children,
  defaultOrganisation,
  defaultLocation,
}) => {
  // State for selections
  const [selectedOrganisation, setSelectedOrganisation] =
    useAtom(organizationAtom);
  const [selectedLocation, setSelectedLocation] = useAtom(locationAtom);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const {
    data: organizations,
    isLoading: isLoadingOrganizations,
    error: organizationError,
    refetch: refetchOrganizations,
  } = useGetOrganizations({
    page: 1,
    perPage: 100,
    sort: 'name',
    order: 'ASC',
    filter: {},
  });

  const {
    data: locations,
    isLoading: isLoadingLocations,
    error: locationError,
    refetch: refetchLocations,
  } = useGetLocations(selectedOrganisation?.id, {
    page: 1,
    perPage: 100,
    sort: 'name',
    order: 'ASC',
  });

  useEffect(() => {
    const savedOrg = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedOrganisation',
      parse: true,
    });
    const savedLoc = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedLocation',
      parse: true,
    });

    const org = savedOrg || defaultOrganisation;
    const loc = savedLoc || defaultLocation;

    if (org) {
      try {
        setSelectedOrganisation(org);
      } catch (e: unknown) {
        console.error('Error parsing saved organisation:', e);
        persistStorageService.removePersistItem({
          name: 'selectedOrganisation',
        });
      }
    }

    if (loc) {
      try {
        setSelectedLocation(loc);
      } catch (e: unknown) {
        console.error('Error parsing saved location:', e);
        persistStorageService.removePersistItem({
          name: 'selectedLocation',
        });
      }
    }

    setIsInitialized(true);
  }, []);

  // Function to select an organisation
  const selectOrganisation = (org: OrgLocation | null) => {
    setSelectedOrganisation(org);
    persistStorageService.removePersistItem({
      name: 'selectedLocation',
    });

    // If org is null, also clear the location selection
    if (!org) {
      setSelectedLocation(null);
      persistStorageService.batchRemovePersistItems({
        names: ['selectedOrganisation', 'selectedLocation'],
      });
    } else {
      persistStorageService.setPersistItem({
        name: 'selectedOrganisation',
        value: JSON.stringify(org),
      });
    }
    queryClient.refetchQueries();
  };

  // Function to select a location
  const selectLocation = (loc: OrgLocation | null) => {
    setSelectedLocation(loc);

    if (!loc) {
      persistStorageService.batchRemovePersistItems({
        names: ['selectedLocation'],
      });
    } else {
      persistStorageService.setPersistItem({
        name: 'selectedLocation',
        value: loc,
        stringify: true,
      });
    }
    queryClient.refetchQueries();
  };

  // Function to clear all selections
  const clearSelections = () => {
    setSelectedOrganisation(null);
    setSelectedLocation(null);
    persistStorageService.batchRemovePersistItems({
      names: ['selectedOrganisation', 'selectedLocation'],
    });
  };

  // Create the context value object
  const contextValue: OrgLocationContextType = {
    organizations: organizations || [],
    selectedOrganisation,
    organisationError: organizationError || null,
    isLoadingOrganisations: isLoadingOrganizations,
    locations: locations || [],
    selectedLocation,
    isLoadingLocations,
    isInitialized,
    locationError,
    selectOrganisation,
    selectLocation,
    clearSelections,
    fetchOrganisationLocations: () => refetchLocations(),
    fetchUserOrganisations: () => refetchOrganizations(),
  };

  return (
    <OrgLocationContext.Provider value={contextValue}>
      {children}
    </OrgLocationContext.Provider>
  );
};

// Custom hook to use the context
export const useOrgLocation = (): OrgLocationContextType => {
  const context = useContext(OrgLocationContext);

  if (context === undefined) {
    throw new Error(
      'useOrgLocation must be used within an OrgLocationProvider',
    );
  }

  return context;
};

// For debugging purposes - to use this in the browser console
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).OrgLocationContext = OrgLocationContext;
}
